import { useEffect, useRef, useState } from 'react';

const REDUCE = '(prefers-reduced-motion: reduce)';
const prefersStill = () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCE).matches;

/*
 * A one-off highlight that plays when `trigger` changes (a step, a picked part),
 * then rests. Returns a number: 0 while resting, otherwise an id that is new for
 * every pulse, so a keyed element restarts its CSS animation even when a second
 * change lands mid-pulse. The CSS owns the animation; `ms` only says when to
 * drop the class (a little after the animation ends). Never under reduced
 * motion. The timer is cleared on unmount.
 *   onMount — also pulse once on arrival (default: only on a change)
 */
export default function usePulse(trigger, { ms = 1800, onMount = false } = {}) {
  const [pulse, setPulse] = useState(0);
  const count = useRef(0);
  const last = useRef(onMount ? {} : trigger); // {} never equals a trigger: pulse on arrival

  useEffect(() => {
    if (Object.is(last.current, trigger)) return;
    last.current = trigger;
    if (prefersStill()) {
      setPulse(0);
      return;
    }
    count.current += 1;
    setPulse(count.current);
  }, [trigger]);

  useEffect(() => {
    if (!pulse) return undefined;
    const t = setTimeout(() => setPulse(0), ms);
    return () => clearTimeout(t);
  }, [pulse, ms]);

  return pulse;
}
